import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import { LCReleaseForm, LCReleaseFormData } from "../../../../types"
import { lang } from "../../../../constants/languages"
import { formatDate2, formatDuration } from "../../../../utility"
import { capitalize } from "lodash"
import AudioPlayer from "../track-detail/AudioPlayer"
import { Dialog } from "@headlessui/react"
import Distributions from "./Distributions"
import { useNavigate } from "react-router-dom"
import { AssetRegistryPages } from "../../../../actions/types"

enum TabOptions {
  TRACKS,
  DSP
}

const ReleaseLCView = ({ releaseForm, options, releaseId }: { releaseForm: LCReleaseForm; options: any, releaseId: string | undefined }) => {
  const [playingAudioRefs, setPlayingAudioRefs] = useState<HTMLAudioElement[]>([])

  const [hoverId, setHoverId] = useState<string>()
  const [songLyrics, setSongLyrics] = useState<string>()
  const [selectedTab, setSelectedTab] = useState(TabOptions.TRACKS)
  
  const navigate = useNavigate()
  
  const getAttribute = (attribute: string) => {
    if (Array.isArray(releaseForm.product.data.attributes[attribute])) {
      // this is added just for artist option for now. it returns an array
      return releaseForm.product.data.attributes[attribute].join(",")
    }
    return releaseForm.product.data.attributes[attribute]
  }

  const renderFields = (propName: string, text: string, value: string, required: boolean, targetTab?:number) => {
    return (
      <div className="flex flex-row">
        <div
          className="flex flex-row gap-2 px-1 rounded-[4px]"
          style={{
            backgroundColor: !value && required ? "rgba(255, 0, 0, 0.2)" : "transparent",
            cursor: !value && required ? "pointer" : "default"
          }}
          onClick={() => {
            if (!value && required) {
              if (!releaseId) return
              navigate(`/catalog/asset-registry/${releaseId}`, {
                state: {propName}
              })
            }
          }}
        >
          {propName !== "copyright-line" && propName !== "production-line" && (
            <div className="flex flex-row gap-2">
              <KSpan text={text} fontWeight={500} color={!value && required ? "#f00" : "#333333"} />
              <KSpan
                text={
                  value
                    ? value
                    : propName === "cover-url" || propName === "booklet"
                    ? lang.catalog.asset_registry.summary.no_file
                    : required
                    ? lang.catalog.asset_registry.summary.missing_item
                    : "-"
                }
                color={!value && required ? "#ff6666" : "#737373"}
                fontStyle={value ? "normal" : "italic"}
              />
            </div>
          )}
          {(propName === "copyright-line" || propName === "production-line") && (
            <div className="flex flex-row gap-2 items-center">
              <div
                className="flex flex-row gap-1 items-center"
                style={{
                  color: value ? "#333333" : "#f00"
                }}
              >
                {propName === "copyright-line" ? (
                  <React.Fragment> &#9400;</React.Fragment>
                ) : (
                  <React.Fragment> &#x24C5;</React.Fragment>
                )}
                <KSpan fontWeight={500} text={text} color={!value && required ? "#f00" : "#333333"} />
              </div>

              <KSpan
                text={value ? value : lang.catalog.asset_registry.summary.missing_item}
                color={!value && required ? "#ff6666" : "#737373"}
                fontStyle={value ? "normal" : "italic"}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderReleaseContributors = () => {
    const contributors = Object.entries(releaseForm.product.data.attributes).filter(
      ([key, value]) => options.roleOptions.includes(key) && value
    )
    return (
      <div className="flex gap-1">
        {contributors.map(([key, value], index) => (
          <div key={`release_lc_view_${key}`} className="flex gap-1">
            <KSpan text={`${capitalize(key)}:`} />
            <KSpan text={value.join(",")} />
            {contributors.length - 1 !== index && <KSpan text={"|"} />}
          </div>
        ))}
      </div>
    )
  }

  const renderTrackContributors = (track: LCReleaseFormData) => {
    const contributors = Object.entries(track.data.attributes).filter(([key, value]) => {
      return options.roleOptions.includes(key) && value
    })

    return (
      <div className="flex flex-wrap w-full" style={{ rowGap: "1.25rem" }}>
        <div className="flex flex-col gap-1 pr-2" style={{ width: "25%" }}>
          <KSpan text={lang.catalog.asset_registry.general_information.artists} />
          <KSpan text={track.data?.attributes["artist"]?.join(", ")} fontWeight={500} color="#111" />
        </div>
        {contributors.map(([key, value], index) => (
          <div
            key={`release_track_view_${track.data.id}_${index}`}
            className="flex flex-col gap-1 pr-2"
            style={{ width: "25%" }}
          >
            <KSpan text={`${capitalize(key)}`} />
            <KSpan text={value.join(", ")} color="#111" fontWeight={500} />
          </div>
        ))}
      </div>
    )
  }

  const renderAssets = (track: LCReleaseFormData) => {
    const opacity16Bit = track.data.attributes?.audio16bits ? "1" : "0.2"
    const opacityis24Bit = track.data.attributes?.audio24bits ? "1" : "0.2"
    const opacityisDolby = track.data.attributes?.["audio-dolby-atmos"] ? "1" : "0.2"

    const missingItem = opacity16Bit === "0.2" && opacityis24Bit === "0.2" && opacityisDolby === "0.2"

    if (missingItem) {
      return <KSpan text={lang.catalog.asset_registry.summary.missing_assets} color="#ff6666" fontStyle="italic" />
    }

    return (
      <div>
        <div className="flex items-center gap-1">
          {track.data.attributes?.audio16bits && (
            <div style={{ background: `rgba(33, 164, 148, ${opacity16Bit})`, borderRadius: 5 }} className="pl-1 pr-1">
              <KSpan text="16-Bit" color="white" />
            </div>
          )}
          {track.data.attributes?.audio24bits && (
            <div style={{ background: `rgba(187, 204, 19, ${opacityis24Bit})`, borderRadius: 5 }} className="pl-1 pr-1">
              <KSpan text="24-Bit" color="white" />
            </div>
          )}

          {track.data.attributes?.["audio-dolby-atmos"] && (
            <div style={{ background: `rgba(48, 39, 44, ${opacityisDolby})`, borderRadius: 5 }} className="p-1">
              <img width={40} height={28} src={"/catalog_icons/dolby.svg"}></img>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderReleaseDetail = () => {
    return (
      <div
        className="flex gap-2 items-center"
        style={{ border: "1px solid #11111120", borderRadius: 5, width: "100%" }}
      >
        <div style={{ minWidth: 320, minHeight: 320 }}>
          <img src={releaseForm.product.data.attributes["full-cover-url"]} style={{ width: 320, height: 320 }} />
        </div>

        <div className="p-2" style={{ width: "100%" }}>
          <div className="mb-4 flex flex-col gap-1 ml-1">
            <KSpan text={releaseForm.product.data.attributes["name"]} fontWeight={500} color="#111" fontSize={16} />
            <KSpan text={releaseForm.product.data.attributes["artist"].join(",")} />
            {renderReleaseContributors()}
          </div>
          <div className="flex flex-row" style={{ width: "100%" }}>
            <div className="flex flex-col grow gap-6" style={{ width: "50%" }}>
              <div className="flex flex-col gap-1">
                {renderFields(
                  "label",
                  `${lang.catalog.asset_registry.general_information.label}:`,
                  options.labelOptions.find(
                    (label) => label.value === releaseForm.product.data?.relationships.label?.data?.id
                  )?.label,
                  true
                )}
                {renderFields(
                  "subgenre",
                  `${lang.catalog.asset_registry.general_information.main_genre}:`,
                  options.distributorProductSubGenreOptions.find(
                    (subgenre) => subgenre.value === releaseForm.offer.data?.relationships["subgenre"]?.data?.id
                  )?.label,
                  true
                )}
                {renderFields(
                  "subgenre2",
                  `${lang.catalog.asset_registry.general_information.secondary_genre}:`,
                  options.distributorProductSubGenreOptions.find(
                    (subgenre2) => subgenre2.value === releaseForm.offer.data.relationships["subgenre2"]?.data?.id
                  )?.label,
                  false
                )}
                {renderFields("upc-code", "UPC:", releaseForm.product.data.attributes["upc-code"], true)}
                {renderFields(
                  "product-type",
                  `${lang.catalog.asset_registry.general_information.product_type}:`,
                  options.productTypeOptions.find(
                    (genre) => genre.value === releaseForm.product.data.relationships["product-type"]?.data?.id
                  )?.label,
                  true
                )}
                {renderFields(
                  "release-date",
                  `${lang.catalog.asset_registry.general_information.release_date}:`,
                  formatDate2(releaseForm.offer.data.attributes["release-date"]),
                  true
                )}
                {renderFields(
                  "original-release-date",
                  `${lang.catalog.asset_registry.summary.original_release_date}:`,
                  formatDate2(getAttribute("original-release-date")),
                  false
                )}
                {renderFields(
                  "created-at",
                  `${lang.catalog.asset_registry.summary.created_on_date}:`,
                  formatDate2(getAttribute("created-at")),
                  false
                )}
              </div>
            </div>
            <div className="grow flex flex-col gap-6" style={{ width: "50%" }}>
              <div className="flex flex-col gap-1">
                {renderFields(
                  "copyright-line",
                  `${lang.catalog.asset_registry.general_information.copyright_line}:`,
                  releaseForm.product.data.attributes["copyright-line"],
                  true
                )}
                {renderFields(
                  "production-line",
                  `${lang.catalog.asset_registry.general_information.production_line}:`,
                  releaseForm.product.data.attributes["production-line"],
                  true
                )}
                {renderFields(
                  "production-year",
                  `${lang.catalog.asset_registry.general_information.production_year}:`,
                  getAttribute("production-year"),
                  false
                )}
                {renderFields(
                  "language",
                  `${lang.catalog.asset_registry.general_information.metadata_language}:`,
                  options.languageOptions.find(
                    (language) => language.value === releaseForm.product.data?.relationships.language?.data?.id
                  )?.label,
                  true
                )}
                {renderFields(
                  "status",
                  `${lang.catalog.release_detail.status}:`,
                  capitalize(getAttribute("status")),
                  false
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderTrackList = () => {
    return (
      <div>
        {releaseForm.tracks.map((track, index: number) => {
          const audioLanguage = options.languageOptions.find(
            (language) => language.value === track?.data.relationships["audio-language"]?.data?.id
          )?.label

          const version = track.data.attributes["subtitle"]
          const versionHeader = version ? ` (${lang.catalog.asset_registry.general_information.version})` : ""
          const versionString = version ? ` (${version})` : ""

          return (
            <div
              key={`track_list_${track.data.id}`}
              className="pt-6 pb-8 pl-1 pr-1"
              style={{ borderBottom: "1px solid #11111160", background: hoverId === track.data.id ? "#11111112" : "" }}
              onMouseEnter={() => setHoverId(track.data.id)}
              onMouseLeave={() => setHoverId(undefined)}
            >
              <div key={`track-${index}`} className="flex flex-row py-1 h-12 gap-2">
                <div className="flex flex-row gap-3 justify-start w-[5%]">
                  <div className="flex flex-col">
                    <KSpan text="#" />
                    <div className="flex gap-2 mt-1 items-center">
                      <KSpan color="#111" text={track.data.attributes["track-number"]} />
                      <AudioPlayer
                        src={track.data.attributes["preview-url"]}
                        playingAudioRefs={playingAudioRefs}
                        setPlayingAudioRefs={setPlayingAudioRefs}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row gap-3 justify-start w-[45%] pr-2">
                  <div className="flex flex-col gap-1">
                    <KSpan text={`${lang.catalog.asset_registry.general_information.title}${versionHeader}`} />
                    <KSpan text={`${track.data.attributes.title}${versionString}`} color="#111" fontWeight={500} />
                  </div>
                </div>
                <div className="w-[25%] flex justify-start">
                  <div className="flex flex-col gap-1">
                    <KSpan text={lang.catalog.asset_registry.summary.isrc} />
                    <KSpan text={track.data?.attributes["isrc-code"]} color="#111" fontWeight={500} />
                  </div>
                </div>
                <div className="w-[13%] flex justify-start pl-1">
                  <div className="flex flex-col gap-1">
                    <KSpan text={lang.catalog.asset_registry.summary.duration} />
                    <KSpan text={formatDuration(track.data.attributes.length)} color="#111" fontWeight={500} />
                  </div>
                </div>
                <div className="w-[12%] flex">
                  <div className="flex flex-col gap-1 items-center pr-1">
                    <KSpan text={lang.catalog.asset_registry.summary.assets} />
                    {renderAssets(track)}
                  </div>
                </div>
              </div>
              <div
                className="flex flex-row pt-4 mt-4 gap-2"
                style={{ borderTop: "1px dashed #11111124", width: "100%" }}
              >
                {renderTrackContributors(track)}
              </div>
              <div className="flex flex-col pt-4 mt-4 gap-2" style={{ borderTop: "1px dashed #11111124" }}>
                <div className="w-full flex">
                  <div className="w-[25%] flex flex-col pr-2">
                    <KSpan text={lang.catalog.asset_registry.general_information.production_line} />
                    <KSpan text={track.data.attributes["production-line"] || "-"} color={"#111"} fontWeight={500} />
                  </div>
                  <div className="w-[25%] flex flex-col pr-2">
                    <KSpan text={lang.catalog.asset_registry.tracks_and_assets.audio_language} />
                    <KSpan text={audioLanguage || "-"} color={"#111"} fontWeight={500} />
                  </div>
                  <div className="w-[25%] flex flex-col pr-2">
                    <KSpan text={lang.catalog.asset_registry.tracks_and_assets.explicit_content} />
                    <KSpan
                      text={
                        track?.data?.attributes["clean"]
                          ? "Clean"
                          : track?.data?.attributes["parental-advisory"]
                          ? "Parental Advisory"
                          : "-"
                      }
                      color={"#111"}
                      fontWeight={500}
                    />
                  </div>
                  <div className="w-[13%] flex flex-col">
                    <KSpan text={lang.catalog.asset_registry.tracks_and_assets.preview_start_time} />
                    <KSpan
                      text={
                        track?.data?.attributes?.["preview-start"]
                          ? formatDuration(track?.data?.attributes?.["preview-start"])
                          : "-"
                      }
                      color={"#111"}
                      fontWeight={500}
                    />
                  </div>
                  <div className="flex w-[12%] pr-1">
                    <div className="flex flex-col">
                      <KSpan text={lang.catalog.asset_registry.tracks_and_assets.lyrics} />
                      <div
                        className={track.data.attributes["lyrics"] ? "cursor-pointer" : ""}
                        onClick={() => setSongLyrics(track.data.attributes["lyrics"])}
                      >
                        <KSpan
                          text={
                            track.data.attributes["lyrics"]
                              ? lang.catalog.asset_registry.tracks_and_assets.see_details
                              : "-"
                          }
                          color={track.data.attributes["lyrics"] ? "rgba(35, 161, 178, 1)" : "#111"}
                          fontWeight={500}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderLyricModal = () => {
    return (
      <Dialog open={songLyrics !== undefined} onClose={() => setSongLyrics(undefined)} className="relative z-50">
        <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div
            className="mt-10 bg-white elev-b-sm rounded-lg overflow-hidden p-6"
            style={{ maxHeight: 600, overflowY: "scroll" }}
          >
            {songLyrics?.split("\n").map((lyric, index) => (
              <div key={`lyric_${index}`}>
                <KSpan text={lyric} />
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <div style={{ width: "100%" }} className="mb-12">
      {renderReleaseDetail()}
      <div style={{ borderBottom: "1px solid #11111160" }} className="pb-4 mt-12 flex gap-6">
        <div className="cursor-pointer" onClick={() => setSelectedTab(TabOptions.TRACKS)}>
          <KSpan
            text={lang.catalog.release_detail.title}
            fontSize={18}
            lineHeight="18px"
            color={selectedTab === TabOptions.TRACKS ? "#111" : "#66666690"}
            fontWeight={selectedTab === TabOptions.TRACKS ? 500 : 400}
          />
        </div>
        <KSpan text="|" />
        <div className="cursor-pointer" onClick={() => setSelectedTab(TabOptions.DSP)}>
          <KSpan
            text={lang.catalog.release_detail.distribution}
            fontSize={18}
            lineHeight="18px"
            color={selectedTab === TabOptions.DSP ? "#111" : "#66666690"}
            fontWeight={selectedTab === TabOptions.DSP ? 500 : 400}
          />
        </div>
      </div>
      {selectedTab === TabOptions.TRACKS && renderTrackList()}
      {selectedTab === TabOptions.DSP && <Distributions releaseForm={releaseForm} options={options} />}
      {renderLyricModal()}
    </div>
  )
}

export default ReleaseLCView
